import { FC, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../utils/api';
import { OfflineNotice } from '../offline';
import { useSnackbar } from 'notistack';

interface Reservation {
  startTime: string;
  endTime: string;
  vetName: string | null;
}

interface BoardRow {
  equipmentId: string;
  name: string;
  type: string;
  // Reservations come from the appointments that booked this unit — so this
  // view is always aligned with the calendar, with nothing to assign by hand.
  reservations: Reservation[];
}

const TYPE_LABELS: Record<string, string> = {
  xray: 'X-ray',
  ultrasound: 'Ultrasound',
  endoscope: 'Endoscope',
  other: 'Other',
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * Schedulable equipment (modalities): the unit registry plus a read-only view
 * of what each unit is reserved for on a given day. Reservations are driven by
 * appointment bookings (the "Equipment Needed" picker), so this stays in sync
 * with the calendar automatically. Lives inside the Inventory page's Equipment
 * tab; distinct from the inventory STOCK items the list below tracks.
 */
const EquipmentPanel: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState(todayStr());
  const [board, setBoard] = useState<BoardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('xray');
  const [saving, setSaving] = useState(false);

  const loadBoard = useCallback(() => {
    setLoading(true);
    api
      .get('/equipment/board', { params: { date } })
      .then((res) => setBoard(res.data || []))
      .catch(() => enqueueSnackbar('Failed to load equipment', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [date, enqueueSnackbar]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const addEquipment = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post('/equipment', { name: newName.trim(), type: newType });
      setAddOpen(false);
      setNewName('');
      setNewType('xray');
      enqueueSnackbar('Equipment added', { variant: 'success' });
      loadBoard();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not add equipment', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <OfflineNotice>
        You're offline — equipment reservations need a connection. The calendar and
        clinical records still work offline.
      </OfflineNotice>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Schedulable Equipment &amp; Modalities
        </Typography>
        <TextField
          type="date"
          size="small"
          label="Day"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add equipment
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Units here can be reserved when booking an appointment (the “Equipment Needed” picker).
        This shows what each unit is reserved for on the selected day.
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          {board.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No equipment yet — add your X‑ray, ultrasound, etc.</Typography>
            </Box>
          ) : (
            board.map((row, i) => (
              <Box
                key={row.equipmentId}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: 2,
                  borderTop: i ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flexShrink: 0, minWidth: 0 }}>
                  <Typography fontWeight={600}>{row.name}</Typography>
                  <Chip size="small" label={TYPE_LABELS[row.type] || row.type} sx={{ mt: 0.5 }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0, textAlign: 'right' }}>
                  {row.reservations.length === 0 ? (
                    <Chip size="small" variant="outlined" label="Available" />
                  ) : (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      {row.reservations.map((r, ri) => (
                        <Chip
                          key={`${r.startTime}-${ri}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${fmtTime(r.startTime)}–${fmtTime(r.endTime)}${
                            r.vetName ? ` · ${r.vetName}` : ''
                          }`}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Paper>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add equipment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Portable X-ray #2"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField select fullWidth label="Type" value={newType} onChange={(e) => setNewType(e.target.value)}>
            <MenuItem value="xray">X-ray</MenuItem>
            <MenuItem value="ultrasound">Ultrasound</MenuItem>
            <MenuItem value="endoscope">Endoscope</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={addEquipment} disabled={saving || !newName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentPanel;
